/**
 * スタンプ帳: 5つの島マップ
 * ボタンの羅列ではなく、各島の画像をマップ上に並べて選択できるUIを提供します。
 * 画面幅へ縮小して収める方式は廃止し、横に広い固定pxワールドをネイティブ横スクロール（ScrollView horizontal）で
 * スワイプ移動する2層構造（ビューポート + ワールド）にすることで、島画像・文字を大きいまま維持します。
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
  type ViewStyle,
} from '../../../shared/components/WebPrimitives';
import { STAMP_ISLANDS } from '../../../shared/constants/stampIslands';

/** 島名ラベル枠の高さ(px) */
const LABEL_BLOCK_HEIGHT = 26;
/** 段間の余白(px) */
const ROW_GAP_Y = 20;
/** 押印スタンプのはみ出しを収める左右余白(px) */
const WORLD_PADDING_X = 22;
/** ワールド上端の余白(px) */
const WORLD_PADDING_TOP = 14;
/** 下部ヒントバー分を含むワールド下端の余白(px) */
const WORLD_PADDING_BOTTOM = 36;
/** ビューポート左右フェードの幅(px) */
const EDGE_FADE_WIDTH = 24;

/** 水面ドット装飾の位置(ワールド基準の%指定のまま流用) */
const WATER_DOTS = Object.freeze([
  { top: '10%', left: '28%', size: 8, opacity: 0.24 },
  { top: '20%', left: '20%', size: 6, opacity: 0.22 },
  { top: '30%', left: '34%', size: 8, opacity: 0.2 },
  { top: '36%', left: '8%', size: 6, opacity: 0.2 },
  { top: '42%', left: '88%', size: 8, opacity: 0.22 },
  { top: '46%', left: '46%', size: 6, opacity: 0.22 },
  { top: '78%', left: '6%', size: 7, opacity: 0.22 },
  { top: '82%', left: '40%', size: 6, opacity: 0.2 },
  { top: '86%', left: '78%', size: 7, opacity: 0.22 },
  { top: '90%', left: '92%', size: 6, opacity: 0.22 },
]);

/** 島ごとのスタンプ回転 */
const STAMP_ROTATION_BY_ISLAND_ID = Object.freeze({
  'island-1': '-10deg',
  'island-2': '8deg',
  'island-3': '-6deg',
  'island-4': '9deg',
  'island-5': '-8deg',
});

/**
 * 画面幅・コンパクト指定から島画像サイズ(px)を判定する
 * @param {boolean} compact - 親からのコンパクト指定(縦が短い端末)
 * @param {number} width - 画面幅
 * @returns {number} 島画像サイズ(px)
 */
const resolveIslandArtSize = (compact: boolean, width: number): number => {
  if (compact) {
    return 132;
  }
  if (width < 480) {
    return 150;
  }
  return 164;
};

/**
 * 島画像サイズからワールド座標一式を計算する
 * 横方向は島画像の透明余白ぶんだけ中心間距離(COLUMN_STEP)を詰めて密度を上げる一方、
 * 縦方向はセル内側View(pt-1/pb-6)を含む実高さがそのまま段の占有高になるため、
 * ROW_STEPはCOLUMN_STEPではなくISLAND_ART_SIZE基準で計算し、上段ラベルと下段バッジの
 * 衝突を防ぐ。
 * @param {number} islandArtSize - 島画像サイズ(px)
 * @returns {{worldWidth: number, worldHeight: number, positions: Record<string, {left: number, top: number}>}} ワールド座標
 */
const computeWorldLayout = (islandArtSize: number) => {
  /** 島の中心間の横距離(px)。画像の透明余白ぶんだけ意図的に詰める */
  const columnStep = Math.round(islandArtSize * 0.86);
  /** 島の中心間の縦距離(px)。上段ラベル・下段バッジの衝突を避けるため島画像サイズ基準 */
  const rowStep = islandArtSize + LABEL_BLOCK_HEIGHT + ROW_GAP_Y;
  const worldWidth = columnStep * 3 + WORLD_PADDING_X * 2;
  const worldHeight =
    rowStep + islandArtSize + LABEL_BLOCK_HEIGHT + WORLD_PADDING_TOP + WORLD_PADDING_BOTTOM;

  const positions: Record<string, { left: number; top: number }> = {
    'island-1': { left: WORLD_PADDING_X + columnStep * 0.5, top: WORLD_PADDING_TOP },
    'island-2': { left: WORLD_PADDING_X + columnStep * 1.5, top: WORLD_PADDING_TOP },
    'island-3': { left: WORLD_PADDING_X + columnStep * 2.5, top: WORLD_PADDING_TOP },
    'island-4': { left: WORLD_PADDING_X + columnStep * 1.0, top: WORLD_PADDING_TOP + rowStep },
    'island-5': { left: WORLD_PADDING_X + columnStep * 2.0, top: WORLD_PADDING_TOP + rowStep },
  };

  return { worldWidth, worldHeight, positions };
};

/**
 * スタンプ帳の島マップ
 * @param {Object} props - props
 * @param {Record<string, { total: number, stamped: number, requiredCount?: number, isCompleted: boolean }>} props.summaryByIslandId - 島別進捗
 * @param {string[]} [props.highlightedIslandIds=[]] - 新規押印を目立たせる島ID一覧
 * @param {(islandId: string) => void} props.onPressIsland - 島押下
 * @param {boolean} [props.compact=false] - コンパクト表示
 * @returns {JSX.Element}
 */
const StampIslandMap = ({
  summaryByIslandId,
  highlightedIslandIds = [],
  onPressIsland,
  compact = false,
}) => {
  /** ScrollViewのimperative handle(scrollToのみ公開) */
  const scrollViewRef = useRef<{ scrollTo: (options: { x?: number; y?: number; animated?: boolean }) => void }>(null);
  /** ScrollViewのonLayoutで取得したビューポート幅(未計測時はnull) */
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);

  /** 新規押印島ID集合 */
  const highlightedIslandIdSet = useMemo(() => {
    return new Set(Array.isArray(highlightedIslandIds) ? highlightedIslandIds : []);
  }, [highlightedIslandIds]);

  /** 画面幅(サイズティア判定専用。ビューポート幅の計測にはScrollViewのonLayoutを使う) */
  const { width } = useWindowDimensions();

  /** 島画像サイズ(px) */
  const islandArtSize = resolveIslandArtSize(compact, width);

  /** ワールド座標一式 */
  const { worldWidth, worldHeight, positions } = useMemo(
    () => computeWorldLayout(islandArtSize),
    [islandArtSize],
  );

  /** ビューポート幅より広く、横スワイプが必要か(未計測の間はtrue扱い) */
  const isScrollable = viewportWidth === null || worldWidth > viewportWidth;

  /**
   * ビューポート幅を計測し、stateへ反映する
   * @param {{ nativeEvent: { layout: { width: number } } }} event - layoutイベント
   * @returns {void}
   */
  const handleViewportLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    setViewportWidth(event.nativeEvent.layout.width);
  };

  useEffect(() => {
    if (viewportWidth === null) {
      return undefined;
    }
    // 画面回転などでビューポート幅が変わった直後はDOM幅の反映前にscrollToが走ることがあるため、
    // 1フレーム遅らせてから中央スクロールを実行する。
    const animationFrameId = window.requestAnimationFrame(() => {
      const centeredX = Math.max(0, (worldWidth - viewportWidth) / 2);
      scrollViewRef.current?.scrollTo({ x: centeredX, animated: false });
    });
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [worldWidth, viewportWidth, islandArtSize]);

  return (
    <View
      className="relative overflow-hidden rounded-none border-4 border-app-quest-night bg-app-quest-sky shadow-[6px_6px_0_rgba(22,36,58,0.32)]"
      style={{ height: worldHeight }}
      testID="stamp-island-map"
    >
      <ScrollView
        accessibilityLabel="島マップ。横にスワイプして移動できます"
        contentContainerStyle={{
          height: worldHeight,
          marginLeft: 'auto',
          marginRight: 'auto',
          width: worldWidth,
        }}
        horizontal
        onLayout={handleViewportLayout}
        ref={scrollViewRef}
        showsHorizontalScrollIndicator={false}
        style={{
          WebkitOverflowScrolling: 'touch',
          height: worldHeight,
          overscrollBehaviorX: 'contain',
          touchAction: 'pan-x',
        } as ViewStyle}
        testID="stamp-island-map-viewport"
      >
        <View
          style={{ height: worldHeight, position: 'relative', width: worldWidth }}
          testID="stamp-island-map-world"
        >
          <View className="absolute inset-0" pointerEvents="none">
            {WATER_DOTS.map((dot, index) => (
              <View
                key={`water-dot-${index}`}
                style={{
                  backgroundColor: '#D6EBFF',
                  height: dot.size,
                  left: dot.left,
                  opacity: dot.opacity,
                  position: 'absolute',
                  top: dot.top,
                  width: dot.size,
                } as ViewStyle}
              />
            ))}
          </View>

          {STAMP_ISLANDS.map((island) => {
            /** ワールド基準の島の中心位置(px) */
            const position = positions[island.id] ?? { left: WORLD_PADDING_X, top: WORLD_PADDING_TOP };
            const summary = summaryByIslandId?.[island.id] ?? { total: 0, stamped: 0, isCompleted: false };
            const hasStamped = Boolean(summary.isCompleted);
            /** 押印必要企画数（未指定・不正時は1、公開企画数の範囲へクランプ） */
            const numericRequiredCount = Number(summary.requiredCount);
            const requiredCount = summary.total > 0
              ? Math.min(
                Math.max(
                  Number.isInteger(numericRequiredCount) ? numericRequiredCount : 1,
                  1,
                ),
                summary.total,
              )
              : 0;
            /** 押印済みは称号獲得を明示し、未押印は必要数に対する進捗を表示する */
            const progressLabel = summary.total <= 0
              ? '準備中'
              : hasStamped
                ? 'スタンプ獲得'
                : `${Math.min(summary.stamped, requiredCount)}/${requiredCount}`;
            const isHighlighted = highlightedIslandIdSet.has(island.id);
            const stampRotation = STAMP_ROTATION_BY_ISLAND_ID[island.id] ?? '0deg';
            const stampImageSize = isHighlighted
              ? Math.round(islandArtSize * 0.46)
              : Math.round(islandArtSize * 0.4);
            const stampBadgeStyle = hasStamped
              ? { bottom: LABEL_BLOCK_HEIGHT + 4, left: 20 }
              : { right: 20, top: 4 };

            return (
              <Pressable
                key={island.id}
                accessibilityRole="button"
                accessibilityLabel={`${island.label} ${hasStamped ? '押印済み' : '未押印'} スタンプ ${progressLabel}`}
                testID={`stamp-island-${island.id}`}
                style={{
                  left: position.left,
                  position: 'absolute',
                  top: position.top,
                  transform: [{ translateX: '-50%' }],
                  width: islandArtSize + 8,
                }}
                hitSlop={{ top: 8, left: 8, bottom: 8, right: 8 }}
                onPress={() => onPressIsland(island.id)}
              >
                <View className="relative items-center px-0.5 pb-6 pt-1">
                  {isHighlighted ? (
                    <View
                      className="absolute rounded-none border-2 border-app-quest-gold bg-[#FFF4C2]"
                      style={{
                        height: Math.round(islandArtSize * 0.8),
                        left: Math.round(islandArtSize * 0.12),
                        opacity: 0.5,
                        right: Math.round(islandArtSize * 0.12),
                        top: Math.round(islandArtSize * 0.1),
                      }}
                      testID={`stamp-highlight-${island.id}`}
                    />
                  ) : null}

                  <Image
                    accessibilityLabel={`${island.label} 島画像`}
                    resizeMode="contain"
                    source={island.artImageSource}
                    style={{ height: islandArtSize, width: islandArtSize }}
                    testID={`stamp-island-art-${island.id}`}
                  />

                  {hasStamped ? (
                    <Image
                      accessibilityLabel={`${island.label} 押印済みスタンプ`}
                      resizeMode="contain"
                      source={island.stampImageSource}
                      style={{
                        height: stampImageSize,
                        position: 'absolute',
                        right: 14,
                        top: 6,
                        transform: [{ rotate: stampRotation }],
                        width: stampImageSize,
                      }}
                      testID={`stamp-image-${island.id}`}
                    />
                  ) : null}

                  <View
                    className={`absolute rounded-none border px-1.5 py-0.5 ${
                      hasStamped ? 'border-app-quest-gold bg-app-quest-night' : 'border-app-quest-night bg-[#FFF8DD]'
                    }`}
                    style={stampBadgeStyle}
                    testID={`stamp-badge-${island.id}`}
                  >
                    <Text
                      className={`font-extrabold tracking-wide ${
                        hasStamped ? 'text-white' : 'text-app-quest-night'
                      } ${compact ? 'text-[10px]' : 'text-[11px]'}`}
                    >
                      {isHighlighted ? 'NEW' : progressLabel}
                    </Text>
                  </View>

                  <View className="absolute bottom-0 left-0 right-0 items-center">
                    <View className="rounded-none border-2 border-app-quest-night bg-[#FFF8DD] px-1.5 py-px">
                      <Text
                        className={`font-extrabold tracking-wide text-app-quest-night ${
                          compact ? 'text-[11px]' : 'text-[12px]'
                        }`}
                      >
                        {island.label}
                      </Text>
                    </View>
                  </View>
                </View>
              </Pressable>
            );
          })}
        </View>
      </ScrollView>

      {isScrollable ? (
        <>
          <View
            className="absolute inset-y-0 left-0"
            pointerEvents="none"
            style={{
              background: 'linear-gradient(90deg, rgba(191,227,255,0.7), rgba(191,227,255,0))',
              width: EDGE_FADE_WIDTH,
            } as ViewStyle}
          />
          <View
            className="absolute inset-y-0 right-0"
            pointerEvents="none"
            style={{
              background: 'linear-gradient(270deg, rgba(191,227,255,0.7), rgba(191,227,255,0))',
              width: EDGE_FADE_WIDTH,
            } as ViewStyle}
          />
        </>
      ) : null}

      <View
        className="absolute bottom-1.5 left-1.5 rounded-none border-2 border-app-quest-night bg-[#FFF8DD] px-1.5 py-0.5"
        pointerEvents="none"
      >
        <Text
          className={`font-extrabold tracking-wide text-app-quest-night ${
            compact ? 'text-[9px]' : 'text-[10px]'
          }`}
          testID="stamp-island-map-hint"
        >
          {isScrollable ? '指で左右に動かして島をタップ' : '島をタップして企画を確認'}
        </Text>
      </View>
    </View>
  );
};

export default StampIslandMap;
